import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { House, Inbox, ListTodo, Menu, Plus, Search, X } from "lucide-react";
import { useRequestCounts } from "@/modules/requests";
import { useMe } from "@/shared/lib/session";
import { BrandMark } from "@/shared/ui/BrandMark";
import { hrefParts } from "./apps";
import { useCreates } from "./creates";
import { createIcon } from "./QuickActions";
import { Sidebar, useHere } from "./Sidebar";

/** Esc closes what's open over the page. */
function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
}

/**
 * The phone's bar at the bottom of the screen: home, search, new, what waits for the user
 * (their requests, else their tasks), and the menu.
 */
export function BottomBar({ onSearch, onNew, onMenu }: { onSearch: () => void; onNew: () => void; onMenu: () => void }) {
  const { t } = useTranslation();
  const { areas, path } = useHere();
  const counts = useRequestCounts();
  const requests = areas.some((a) => a.key === "requests");
  const tasks = areas.some((a) => a.pages.some((p) => p.to === "/tasks"));
  const cell = "flex flex-col items-center justify-center gap-1 py-1.5 text-[11px]";
  const on = (active: boolean) => (active ? "font-semibold text-ledger" : "text-ink-3");
  const waiting = counts.data?.waiting ?? 0;
  return (
    <nav aria-label={t("shell.bottomBar")} className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-line bg-surface/95 px-2 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden">
      <Link to="/" className={clsx(cell, on(path === "/"))}><House className="size-[22px]" aria-hidden />{t("nav.home")}</Link>
      <button type="button" onClick={onSearch} className={clsx(cell, on(false))}><Search className="size-[22px]" aria-hidden />{t("common.search")}</button>
      <div className="flex items-start justify-center">
        <button type="button" onClick={onNew} aria-label={t("create.title")} className="-mt-4 flex size-[52px] items-center justify-center rounded-full bg-ledger text-white shadow-lg shadow-ledger/25 active:scale-95">
          <Plus className="size-6" aria-hidden />
        </button>
      </div>
      {requests ? (
        <Link to="/requests" className={clsx(cell, "relative", on(path.startsWith("/requests")))}>
          <Inbox className="size-[22px]" aria-hidden />{t("areas.requests")}
          {waiting > 0 && <span className="num absolute top-0.5 start-[calc(50%+4px)] rounded-full bg-saffron px-1.5 text-[10px] font-semibold text-white">{waiting}</span>}
        </Link>
      ) : tasks ? (
        <Link to="/tasks" className={clsx(cell, on(path.startsWith("/tasks")))}><ListTodo className="size-[22px]" aria-hidden />{t("nav.myTasks")}</Link>
      ) : <span />}
      <button type="button" onClick={onMenu} className={clsx(cell, on(false))}><Menu className="size-[22px]" aria-hidden />{t("shell.menu")}</button>
    </nav>
  );
}

/** The whole menu on a phone, from the side the page starts on. */
export function MenuDrawer({ open, onClose, top }: { open: boolean; onClose: () => void; top?: React.ReactNode }) {
  const { t } = useTranslation();
  const brand = useMe().data?.tenant.brand;
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label={t("shell.menu")}>
      <button type="button" aria-label={t("common.close")} className="fade-in absolute inset-0 bg-ink/30" onClick={onClose} />
      <aside className="drawer-start absolute inset-y-0 start-0 flex w-[300px] max-w-[85vw] flex-col bg-paper shadow-xl">
        <div className="flex h-14 shrink-0 items-center justify-between px-5">
          <BrandMark brand={brand} className="h-5 w-auto" />
          <button type="button" className="btn-ghost p-1.5" onClick={onClose} aria-label={t("common.close")}><X className="size-4" /></button>
        </div>
        {top && <div className="px-3 pb-1">{top}</div>}
        <div className="min-h-0 flex-1"><Sidebar drawer onNavigate={onClose} /></div>
      </aside>
    </div>
  );
}

/** "New" on a phone: everything the user may create, most used first, in a sheet from the bottom. */
export function NewSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const creates = useCreates();
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label={t("create.title")}>
      <button type="button" aria-label={t("common.close")} className="fade-in absolute inset-0 bg-ink/30" onClick={onClose} />
      <div className="sheet-up absolute inset-x-0 bottom-0 max-h-[80dvh] overflow-y-auto rounded-t-[22px] bg-surface px-4 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-line" aria-hidden />
        <div className="mb-3 px-2 text-[17px] font-bold">{t("create.title")}</div>
        <div className="grid grid-cols-2 gap-2">
          {creates.map((c) => {
            const Icon = createIcon[c.label] ?? Plus;
            const { to, search } = hrefParts(c.to);
            return (
              <Link key={c.to} to={to} search={search as never} onClick={onClose}
                className="flex items-center gap-2.5 rounded-xl bg-paper px-3 py-3 text-[14.5px] font-medium active:bg-line-2">
                <Icon className="size-[18px] shrink-0 text-ledger" aria-hidden />{t(c.label)}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
